import {
  isValidPhoneNumber,
  isPossiblePhoneNumber,
  parsePhoneNumber,
  getCountryCallingCode,
} from "react-phone-number-input";

/**
 * Phone validation utility module
 *
 * This module centralizes phone number validation logic following the Single Responsibility Principle.
 * Each function has a clear, focused purpose and returns predictable results (pure functions).
 *
 * Design Principles Applied:
 * - SRP: Each function validates one specific aspect
 * - Pure Functions: No side effects, predictable outputs
 * - DRY: Reusable validation logic
 * - Clear naming: Function names describe what they validate
 */

/**
 * Validates if a phone number is completely valid for its country
 * This is the strictest validation - checks format, length, and number plan
 *
 * @param {string} phoneNumber - The phone number to validate (E.164 format preferred)
 * @returns {boolean} True if the phone number is valid
 */
export const validatePhoneNumber = (phoneNumber) => {
  if (!phoneNumber || typeof phoneNumber !== "string") {
    return false;
  }

  try {
    return isValidPhoneNumber(phoneNumber);
  } catch (error) {
    // Handle any parsing errors gracefully
    return false;
  }
};

/**
 * Checks if a phone number could possibly be valid (more lenient than validatePhoneNumber)
 * This checks if the number has the right format but may not verify exact length
 *
 * @param {string} phoneNumber - The phone number to check
 * @returns {boolean} True if the phone number could be valid
 */
export const isPossiblePhone = (phoneNumber) => {
  if (!phoneNumber || typeof phoneNumber !== "string") {
    return false;
  }

  try {
    return isPossiblePhoneNumber(phoneNumber);
  } catch (error) {
    return false;
  }
};

/**
 * Gets detailed validation error for a phone number
 * Returns specific error messages to guide users
 *
 * @param {string} phoneNumber - The phone number to validate
 * @param {object} translations - Translation function or object for i18n
 * @returns {string|null} Error message or null if valid
 */
export const getPhoneValidationError = (phoneNumber, translations = {}) => {
  // Default error messages
  const defaultMessages = {
    required: "Phone number is required",
    invalidFormat: "Invalid phone number format",
    invalidLength: "Invalid phone number length for this country",
    tooShort: "Phone number is too short",
    tooLong: "Phone number is too long",
    invalidCountryCode: "Invalid country code",
  };

  // Merge with provided translations
  const messages = { ...defaultMessages, ...translations };

  // Check if phone number is provided
  if (!phoneNumber || phoneNumber.trim() === "") {
    return messages.required;
  }

  // Check if it's a possible phone number first (basic format check)
  if (!isPossiblePhone(phoneNumber)) {
    return messages.invalidFormat;
  }

  // Check strict validation
  if (!validatePhoneNumber(phoneNumber)) {
    try {
      const parsed = parsePhoneNumber(phoneNumber);

      // Try to provide more specific error based on the issue
      if (parsed) {
        const numberLength = parsed.nationalNumber.length;
        const country = parsed.country;

        // You could add country-specific validation rules here
        // For now, return a general length error
        return messages.invalidLength;
      }
    } catch (error) {
      // If we can't parse it, it's a format issue
      return messages.invalidFormat;
    }

    return messages.invalidLength;
  }

  // Phone number is valid
  return null;
};

/**
 * Extracts phone number components for display or processing
 *
 * @param {string} phoneNumber - The phone number to parse
 * @returns {object|null} Parsed phone components or null if invalid
 */
export const parsePhoneComponents = (phoneNumber) => {
  if (!phoneNumber || !validatePhoneNumber(phoneNumber)) {
    return null;
  }

  try {
    const parsed = parsePhoneNumber(phoneNumber);

    return {
      country: parsed.country,
      countryCallingCode: parsed.countryCallingCode,
      nationalNumber: parsed.nationalNumber,
      number: parsed.number,
      formatInternational: parsed.formatInternational(),
      formatNational: parsed.formatNational(),
      uri: parsed.getURI(),
    };
  } catch (error) {
    return null;
  }
};

/**
 * Formats a phone number for display
 *
 * @param {string} phoneNumber - The phone number to format
 * @param {string} format - Format type: 'international', 'national', 'uri'
 * @returns {string} Formatted phone number or original if invalid
 */
export const formatPhoneNumber = (phoneNumber, format = "international") => {
  if (!phoneNumber) return "";

  try {
    const parsed = parsePhoneNumber(phoneNumber);

    if (!parsed) return phoneNumber;

    switch (format) {
      case "national":
        return parsed.formatNational();
      case "uri":
        return parsed.getURI();
      case "international":
      default:
        return parsed.formatInternational();
    }
  } catch (error) {
    // Return original if parsing fails
    return phoneNumber;
  }
};

/**
 * Validates specific problematic phone numbers
 * This function specifically checks for the invalid numbers mentioned in the requirements
 *
 * @param {string} phoneNumber - The phone number to validate
 * @returns {object} Validation result with isValid flag and specific error
 */
export const validateProblematicNumbers = (phoneNumber) => {
  const problematicPatterns = [
    {
      // French numbers should have exactly 9 digits after +33
      pattern: /^\+33\d{10,}$/,
      error: "French phone numbers should have exactly 9 digits after country code",
    },
    {
      // US/Canada numbers should have exactly 10 digits after +1
      pattern: /^\+1\d{11,}$/,
      error: "US/Canada phone numbers should have exactly 10 digits after country code",
    },
    {
      // Peru mobile numbers start with 9, landlines have specific area codes
      pattern: /^\+515\d{8,}$/,
      error: "Invalid Peru phone number format",
    },
  ];

  for (const { pattern, error } of problematicPatterns) {
    if (pattern.test(phoneNumber)) {
      return { isValid: false, error };
    }
  }

  // Use standard validation for other cases
  const error = getPhoneValidationError(phoneNumber);
  return { isValid: !error, error };
};

/**
 * Export all validation functions as a cohesive API
 * Following the Facade pattern to provide a simple interface
 */
export default {
  validate: validatePhoneNumber,
  isPossible: isPossiblePhone,
  getError: getPhoneValidationError,
  parse: parsePhoneComponents,
  format: formatPhoneNumber,
  validateProblematic: validateProblematicNumbers,
};
